import React from "react";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from "../../../components/common/Icons/Icons";

// Mail-style reader shared by Promos and Notifications: the message list, then the open
// message. Each list keeps its own logic (e.g. marking notifications read) and tells the
// reader how its items describe themselves.
interface NewsReaderProps {
  title: string;
  total: number;
  items: any[];
  loading: boolean;
  emptyText: string;
  activeIndex: number;
  onSelect: (index: number) => void;
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  // Shown in the thumbnail when an item has no image
  icon: React.ReactNode;
  bodyOf: (item: any) => string;
  // Right side of a list row: short date, "5d left"
  listAside: (item: any) => React.ReactNode;
  // Under the title in the open message
  detailMeta: (item: any) => React.ReactNode;
  // Left of the button in the message's footer bar
  footerNote?: (item: any) => React.ReactNode;
  ctaLabel: (item: any) => string;
  isUnread?: (item: any) => boolean;
  // Small tag above the title ("Promo", "Notification")
  kind: string;
}

const NewsReader: React.FC<NewsReaderProps> = ({
  title, total, items, loading, emptyText, activeIndex, onSelect, page, totalPages, onPage,
  icon, bodyOf, listAside, detailMeta, footerNote, ctaLabel, isUnread = () => false, kind,
}) => {
  const active = items[activeIndex];

  return (
    <div className="nw-reader">
      <section className="nw-list" aria-label={title}>
        <header className="nw-list-head">
          <h2 className="nw-list-title">{title}</h2>
          {!loading && <span className="nw-list-count">{total}</span>}
        </header>

        {loading ? (
          <div className="nw-state"><span className="spinner" role="progressbar" aria-label="Loading"/></div>
        ) : items.length === 0 ? (
          <div className="nw-state"><span className="nw-empty">{emptyText}</span></div>
        ) : (
          <ul className="nw-items">
            {items.map((item: any, i: number) => {
              const unread = isUnread(item);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`nw-item ${i === activeIndex ? 'active' : ''} ${unread ? 'unread' : ''}`}
                    aria-current={i === activeIndex ? 'true' : undefined}
                    onClick={() => onSelect(i)}
                  >
                    <span className="nw-thumb" aria-hidden="true">
                      {item.image_url ? <img src={item.image_url} alt=""/> : icon}
                    </span>
                    <span className="nw-item-text">
                      <span className="nw-item-top">
                        <span className="nw-item-title">
                          {unread && <span className="visually-hidden">Unread: </span>}
                          {item.title}
                        </span>
                        <span className="nw-item-aside">{listAside(item)}</span>
                      </span>
                      <span className="nw-item-preview">{bodyOf(item)}</span>
                    </span>
                    {unread && <span className="nw-unread-dot" aria-hidden="true"/>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="nw-pager">
            <button type="button" className="btn btn-sm btn-ghost icon-btn" disabled={page <= 1 || loading} onClick={() => onPage(page - 1)} aria-label="Previous page">
              <ChevronLeftIcon/>
            </button>
            <span className="nw-pager-status">Page {page} of {totalPages}</span>
            <button type="button" className="btn btn-sm btn-ghost icon-btn" disabled={page >= totalPages || loading} onClick={() => onPage(page + 1)} aria-label="Next page">
              <ChevronRightIcon/>
            </button>
          </div>
        )}
      </section>

      {active && !loading ? (
        // Keyed by message so each one fades in when opened
        <article key={active.id} className="nw-message" aria-live="polite">
          <div className="nw-message-scroll">
            {active.image_url && (
              // The whole image, framed by a soft blurred copy of itself
              <div className="nw-banner" style={{'--nw-img': `url("${active.image_url}")`} as React.CSSProperties}>
                <img src={active.image_url} alt={active.title}/>
              </div>
            )}

            <div className="nw-message-body">
              <span className="nw-kind">{icon}{kind}</span>
              <h1 className="nw-message-title">{active.title}</h1>
              <div className="nw-message-meta">{detailMeta(active)}</div>

              <p className="nw-text">{bodyOf(active)}</p>
            </div>
          </div>

          {active.url && (
            <footer className="nw-message-foot">
              <span className="nw-foot-note">{footerNote?.(active)}</span>
              <a className="btn btn-primary nw-cta" href={active.url} target="_blank" rel="noopener noreferrer">
                {ctaLabel(active)}<ExternalLinkIcon/>
              </a>
            </footer>
          )}
        </article>
      ) : (
        <div className="nw-message nw-message-empty" aria-hidden="true">
          <span className="nw-message-empty-icon">{icon}</span>
        </div>
      )}
    </div>
  );
};

export default NewsReader;
